import { IsDateValidate, IsDecimalNumber, RequireProperty } from '@agroone/helpers'
import { plainToInstance, Transform, Type } from 'class-transformer'
import {
  IsArray,
  IsBoolean,
  IsDateString,
  IsEnum,
  IsIn,
  IsInt,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsPositive,
  IsString,
  Length,
  Max,
  Min,
  Validate,
  ValidateIf,
  ValidateNested,
} from 'class-validator'

import { DateFormat } from '@agroone/dates'
import { SeedingRateUnit } from '../enums/seeding-rate-unit.enum'
import { SaveFeatureCollection } from './save-feature-collection'
import { SaveLocation } from './save-location'
import { SaveReporting } from './save-reporting'

export class SavePlantingContractorArea {
  @IsInt()
  @IsNotEmpty()
  contractorId!: number

  @IsNumber()
  @Min(0.1)
  @IsNotEmpty()
  @Validate(IsDecimalNumber, [2])
  areaPlanted!: number
}

export class SaveCropPlanting {
  @IsString()
  @IsOptional()
  plantingId: string | undefined

  @IsNumber()
  @Min(0)
  @IsOptional()
  @Validate(IsDecimalNumber, [2])
  @RequireProperty('seedingRateUnit')
  seedingRate: number | undefined

  @IsEnum(SeedingRateUnit)
  @IsOptional()
  @RequireProperty('seedingRate')
  seedingRateUnit?: SeedingRateUnit

  @IsOptional()
  @Length(10)
  @IsDateValidate(DateFormat.YYYY_MM_DD)
  germinationDate?: string

  @IsOptional()
  @IsString()
  weatherConditions?: string

  @IsOptional()
  @IsString()
  soilConditions?: string

  @IsOptional()
  @IsString()
  plantingNote?: string

  @IsInt()
  @Min(1)
  @IsOptional()
  beds?: number

  @IsInt()
  @Min(1)
  @IsOptional()
  seedLines?: number

  @IsNumber()
  @Min(0.01)
  @IsOptional()
  @RequireProperty('bedWidthUnit')
  bedWidth?: number

  @IsOptional()
  @IsString()
  @RequireProperty('bedWidth')
  @IsIn(['in', 'cm', 'mm'])
  bedWidthUnit?: string

  @IsBoolean()
  @IsOptional()
  plantingValidated?: boolean | undefined

  @IsInt()
  @Min(0)
  @IsOptional()
  @RequireProperty('seedSpacingUnit')
  seedSpacing?: number

  @IsOptional()
  @IsString()
  @RequireProperty('seedSpacing')
  @IsIn(['in', 'cm', 'mm'])
  seedSpacingUnit?: string

  @IsOptional()
  @IsDateValidate(DateFormat.DATETIME_OFFSET)
  initialPlannedHarvestDate?: string

  @IsString()
  @IsOptional()
  lotNumber!: string

  @IsNumber()
  @IsOptional()
  @RequireProperty('depthOfPlantingUnits')
  depthOfPlanting?: number

  @IsString()
  @IsOptional()
  @RequireProperty('depthOfPlanting')
  depthOfPlantingUnits?: string

  @IsString()
  @IsOptional()
  fieldBuffer?: string

  @IsBoolean()
  @IsOptional()
  rolledAfterSeed?: boolean

  @IsNumber()
  @IsOptional()
  @IsInt()
  specificationId?: number

  @IsNumber()
  @IsOptional()
  @IsInt()
  sectorId?: number

  @IsNumber()
  @Min(0)
  @IsOptional()
  pivotArea?: number

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => SavePlantingContractorArea)
  @IsOptional()
  contractorAreas?: SavePlantingContractorArea[]
}

export class SavePreClosingCancelEntry {
  @IsString()
  @IsOptional()
  cause?: string

  @IsNumber()
  @Min(0)
  @IsOptional()
  area?: number

  @IsString()
  @IsOptional()
  unit?: string
}

export class SavePreClosingTrialEntry {
  @IsString()
  @IsOptional()
  cause?: string

  @IsNumber()
  @Min(0)
  @IsOptional()
  area?: number

  @IsString()
  @IsOptional()
  unit?: string
}

export class SavePreClosing {
  @IsOptional()
  @IsDateValidate(DateFormat.YYYY_MM_DD)
  harvestEndDate?: string

  @IsString()
  @IsOptional()
  mixedSite?: string

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => SavePreClosingCancelEntry)
  cancelEntries?: SavePreClosingCancelEntry[]

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => SavePreClosingTrialEntry)
  trialEntries?: SavePreClosingTrialEntry[]

  @IsBoolean()
  @IsOptional()
  irrigated?: boolean

  @IsString()
  @IsOptional()
  note?: string
}

export class SaveCrop extends SaveReporting {
  public static override build(raw: any) {
    return plainToInstance(SaveCrop, raw)
  }

  @IsNumber()
  @IsOptional()
  id?: number

  @IsString()
  @Length(10)
  @IsOptional()
  cropName?: string

  @IsString()
  @Length(5)
  @IsOptional()
  shortName?: string

  @Length(10)
  @IsDateString()
  @IsOptional()
  plantedDate?: string

  @IsNumber()
  @Validate(IsDecimalNumber, [4])
  @IsOptional()
  plantedArea?: number

  @IsNumber()
  @Min(0)
  @Validate(IsDecimalNumber, [4])
  @IsNotEmpty()
  intendedArea!: number

  @IsString()
  @IsNotEmpty()
  areaUnit!: string

  @Transform(({ value }) => (value === '' || value === undefined ? null : value))
  @IsNumber()
  @IsOptional()
  varietyId?: number | null

  @IsNumber()
  @IsNotEmpty()
  cropTypeId!: number

  @ValidateIf((o) => !o.isVirtual)
  @IsNumber()
  @IsNotEmpty()
  fieldId!: number

  @IsOptional()
  @IsDateValidate(DateFormat.DATETIME_OFFSET)
  startDate?: string

  @IsOptional()
  @IsDateValidate(DateFormat.DATETIME_OFFSET)
  endDate?: string

  @IsNumber()
  @IsNotEmpty()
  campaignId!: number

  @IsOptional()
  @IsInt()
  @IsPositive()
  countryId?: number | null

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveLocation)
  location?: SaveLocation

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveCropPlanting)
  planting!: SaveCropPlanting

  @IsOptional()
  @ValidateNested()
  @Type(() => SavePreClosing)
  preClosing?: SavePreClosing

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveFeatureCollection)
  boundary?: SaveFeatureCollection

  @IsOptional()
  @IsDateValidate(DateFormat.DATETIME_OFFSET)
  plannedHarvestDate?: string

  @Min(0)
  @IsInt()
  @IsOptional()
  technician?: number

  @IsOptional()
  @IsDateValidate(DateFormat.DATETIME_OFFSET)
  forecastHarvestDate?: string

  @IsString()
  @IsOptional()
  associatedCrop?: string

  @IsString()
  @IsOptional()
  growingSystem?: string

  @IsString()
  @IsOptional()
  notes?: string

  @IsBoolean()
  @IsOptional()
  active?: boolean

  @IsNumber()
  @IsOptional()
  userId?: number

  @IsString()
  @IsOptional()
  externalId?: string

  @IsInt()
  @IsPositive()
  @IsOptional()
  externalClientId?: number

  @IsNumber()
  @IsOptional()
  destinationFactoryId?: number

  @IsString()
  @IsNotEmpty()
  region!: string

  @IsOptional()
  @IsDateValidate(DateFormat.DATETIME_OFFSET)
  lastScoutingDate?: string | null

  @IsNumber()
  @IsOptional()
  lastTimeCropWasGrown?: number

  @IsString()
  @IsOptional()
  lastFloweringDate?: string

  @Transform(({ value }) => (value != null && value !== '' ? Number(value) : undefined))
  @IsNumber()
  @IsOptional()
  floweringScoutingId?: number

  @Transform(({ value }) => (value != null && value !== '' ? Number(value) : undefined))
  @IsNumber()
  @IsOptional()
  yieldEstimationScoutingId?: number | null

  @IsNumber()
  @IsOptional()
  cooperativeId?: number

  @IsNumber()
  @IsOptional()
  growthStageScoutingId?: number | null

  @IsOptional()
  @IsDateValidate(DateFormat.DATETIME_OFFSET)
  reentryDate?: string | null

  @IsOptional()
  @IsArray()
  @IsInt({ each: true })
  @Min(1, { each: true })
  productsUsedTwoYearsAgo?: number[]

  @IsOptional()
  @IsArray()
  @IsInt({ each: true })
  @Min(1, { each: true })
  productsUsedLastYear?: number[]

  @IsOptional()
  @IsArray()
  @Type(() => SaveCropGrowPreviousYear)
  @ValidateNested({ each: true })
  growsLastYear?: SaveCropGrowPreviousYear[]

  @IsOptional()
  @IsArray()
  @Type(() => SaveCropGrowPreviousYear)
  @ValidateNested({ each: true })
  growsTwoYearsAgo?: SaveCropGrowPreviousYear[]

  @IsOptional()
  @IsBoolean()
  nutTrees?: boolean

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  problematicWeeds?: string[]

  @IsOptional()
  @IsBoolean()
  isVirtual?: boolean

  @IsOptional()
  @IsString()
  supplierReference?: string

  @ValidateIf((o) => o.isVirtual)
  @IsInt()
  @IsPositive()
  farmId?: number

  @IsOptional()
  @IsBoolean()
  isFromVirtualCrop?: boolean

  @ValidateIf((o) => o.isFromVirtualCrop)
  @IsInt()
  @IsPositive()
  virtualCropId?: number

  @IsOptional()
  @IsString()
  tillageCondition?: string

  @IsOptional()
  @IsString()
  town?: string

  @IsOptional()
  @IsString()
  zipCode?: string

  @IsOptional()
  @Transform(({ value }) => (value === '' || value === undefined ? null : value))
  @IsInt()
  @IsPositive()
  varietalTypeId?: number | null
}

export class SaveCropGrowPreviousYear {
  @IsNumber()
  @IsInt()
  @IsPositive()
  constantKeyId!: number

  @IsNumber()
  @IsPositive()
  @Max(999999.99)
  area!: number

  @IsString()
  @IsNotEmpty()
  unit!: string
}
